import { CellRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/ContractTable/CellRenderer';
import { ColumnRenderer } from '@dealstack/frontend/shared/components/Investors/Substeps/ContractTable/ColumnRenderer';
import { ColumnDefinition } from '@dealstack/frontend/shared/components/Table/Table/types';
import { useTable } from '@dealstack/frontend/shared/components/Table/Table/useTable';
import { TableCheckbox } from '@dealstack/frontend/shared/components/Table/DefaultTable/TableComponents';
import { DataContractTct, PostInvestorDraftInstanceRes } from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

export const useContractTable = (
	areAllInvestorsSelected: (contractId: string) => boolean,
	onContractSelectAll: (contractId: string) => void,
	onContractCheckboxClick: (contractId: string, investorId: string) => void,
	contractsChecked?: Map<string, Set<string>>,
	investors?: PostInvestorDraftInstanceRes[],
	contracts?: DataContractTct[]
) => {
	const commonUiTranslation = useTranslationCommonUi();

	const columns: ColumnDefinition[] = useMemo(
		() => [
			{
				id: 'name',
				label: commonUiTranslation('investors.name'),
				sortable: true,
				pinned: true,
				width: 150,
			},
			{
				id: 'contact',
				label: commonUiTranslation('investors.contact'),
				pinned: true,
				width: 300,
				subcolumns: [
					{
						id: 'email',
						label: commonUiTranslation('investors.email'),
						sortable: true,
					},
				],
			},
			{
				id: 'contracts',
				label: commonUiTranslation('investors.contract_documentation'),
				pinned: false,
				subcolumns: contracts?.map((contract) => ({
					id: `contracts-${contract.id}`,
					label: contract.name,
					pinned: false,
				})),
			},
		],
		[contracts]
	);

	const data = useMemo(
		() => [
			...(investors?.map((investor, index) => ({
				...investor,
				id: investor.email,
				contact: {
					email: investor.email,
				},
				name: `${investor.firstName} ${investor.lastName}`,
				rowIndex: index,
			})) ?? []),
		],
		[investors]
	);

	const tableInstance = useTable({
		columns,
		data,
		totalCount: data.length,
		tableHeight: 500,
		rowSize: 56,
		rowSelect: false,
		customCellValueFn: useCallback(
			(column, item) => {
				if (column.id === 'name') {
					return item.name;
				} else if (column.id === 'email') {
					return item.contact.email;
				} else if (column.id.startsWith('contracts-')) {
					const contractIdArray = column.id.split('-');
					contractIdArray.shift();
					const contractId = contractIdArray.join('-');

					const isChecked = item.selectAll
						? areAllInvestorsSelected(contractId)
						: contractsChecked?.get(contractId)?.has(item.email) ?? false;

					return (
						<TableCheckbox
							checked={isChecked}
							onClick={
								item.selectAll
									? () => onContractSelectAll(contractId)
									: () => onContractCheckboxClick(contractId, item.email)
							}
							className="w-fit"
							index={item.rowIndex}
						/>
					);
				} else {
					return '??';
				}
			},
			[contractsChecked]
		),
		CustomCellRenderer: CellRenderer,
		CustomColumnRenderer: ColumnRenderer({ areAllInvestorsSelected, onContractSelectAll }),
	});

	return { tableInstance };
};
